<template>
    <MainContentSearchable
        title="Listado de solicitudes preventa"
        id="preventa"
        v-model="filters"
        :disabled="loading"
        :meta="loading ? {} : registros?.meta"
        @updated="debouncedGetRegistros"
    >
        <template v-slot:header>
            <CardsInfoEntradas
                :evento_id="evento_id"
            />
        </template>
        <template v-slot:extra-options>
            <div class="col-12 col-lg-9 mt-2">
                <div class="d-flex flex-column flex-lg-row align-items-lg-center">
                    <div class="d-flex flex-column flex-md-row flex-grow-1">
                        <select
                            class="custom-select mb-2 mb-md-0 mr-md-2"
                            v-model="filters.status"
                            :disabled="loading"
                            @change="debouncedGetRegistros()"
                        >
                            <option value="">Estado</option>
                            <option value="pending">Pendiente</option>
                            <option value="selected">Aprobado</option>
                            <option value="rejected">Rechazado</option>
                            <option value="purchased">Comprado</option>
                        </select>
                        <select
                            class="custom-select mb-2 mb-md-0 mr-md-2"
                            v-model="filters.sortBy"
                            :disabled="loading"
                            @change="debouncedGetRegistros()"
                        >
                            <option value="created_at">Ordenar por fecha</option>
                            <option value="n_entradas">Ordenar por nº entradas</option>
                            <option value="id">Ordenar por ID</option>
                            <option value="risk_score">Ordenar por riesgo</option>
                        </select>
                        <select
                            class="custom-select"
                            v-model="filters.order"
                            :disabled="loading"
                            @change="debouncedGetRegistros()"
                        >
                            <option value="desc">Descendente</option>
                            <option value="asc">Ascendente</option>
                        </select>

                    </div>
                    <div class="d-flex flex-column flex-md-row mt-4 mt-lg-0 ml-lg-5">
                        <button
                            class="btn btn-sm btn-primary mb-2 mb-md-0 mr-md-2"
                            :disabled="loading"
                            @click="showModal('gestionar-solicitudes')"
                        >
                            <i class="fa fa-tasks mr-1"></i> Gestionar solicitudes
                        </button>
                        <button
                            class="btn btn-sm btn-primary"
                            :disabled="loading"
                            @click="showModal('exportar-preventa')"
                        >
                            <i class="fa fa-download mr-1"></i> Exportar Excel
                        </button>
                    </div>
                </div>
            </div>
            <GestionarSolicitudes
                name="gestionar-solicitudes"
                :evento_id="evento_id"
                @close="closeModal"
            />
            <DescargarInformePreventaModal
                name="exportar-preventa"
                :evento_id="evento_id"
                @close="closeModal"
            />
            <CriteriosRiesgoPreventa
                name="criterios-riesgo-preventa"
                @close="closeModal"
            />
        </template>
        <template v-slot:content>
            <div v-if="!loading && registros" :class="['table', { 'expanded': registros.data?.length <= 3 }]">
                <table :class="['table', { 'expanded': registros.data?.length <= 3 }, 'table-hover']">
                    <thead>
                        <tr>
                            <th>Nombre</th>
                            <th>Email</th>
                            <th>Teléfono</th>
                            <th class="text-center">Nº Entradas</th>
                            <th class="text-center">
                                Riesgo
                                <span
                                    class="fa fa-info-circle text-primary"
                                    style="cursor: pointer;"
                                    v-b-tooltip.hover
                                    title="Haz click para saber más información sobre el nivel de riesgo"
                                    @click="showModal('criterios-riesgo-preventa')"
                                ></span>
                            </th>
                            <th class="text-center">Estado</th>
                            <th class="text-center">Opciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        <template v-for="(registro, index) in registros.data">
                            <tr :key="index">
                                <td>{{ registro.user.name }}</td>
                                <td>{{ registro.user.email }}</td>
                                <td>{{ registro.user.telefono }}</td>
                                <td class="text-center font-weight-bold">
                                    {{ registro.n_entradas }}
                                </td>
                                <td class="text-center">
                                    <Riesgo
                                        v-if="registro && registro.risk_score != null"
                                        :riskScore="registro.risk_score"
                                    />
                                    <span v-else> - </span>
                                </td>
                                <td class="text-center">
                                    <span :class="getBadgeClass(registro.status)">
                                        {{ getStatusLabel(registro.status) }}
                                    </span>
                                </td>
                                <td class="text-center">
                                    <OpcionesDropdownTableComponent
                                        :lastIndex="registros.data.length > 3 && index >= registros.data.length - 3"
                                        :options="getOptions(registro)"
                                    />
                                </td>
                                <ListadoPreventaModal
                                    :registro="registro"
                                    :evento_id="evento_id"
                                    @close="closeModal"
                                />
                            </tr>
                        </template>
                    </tbody>
                </table>
            </div>
            <div v-else class="text-center my-5">
                <b-spinner variant="primary" label="Cargando..."/>
            </div>
        </template>
    </MainContentSearchable>
</template>

<script setup>
    import { onMounted, watch } from 'vue';
    import { getCurrentInstance } from 'vue';
    import MainContentSearchable from '@/components/backend/components/MainContentSearchable';
    import OpcionesDropdownTableComponent from '@/components/backend/components/OpcionesDropdownTableComponent';
    import ListadoPreventaModal from './modals/ListadoPreventaModal';
    import DescargarInformePreventaModal from './modals/DescargarInformePreventaModal.vue';
    import GestionarSolicitudes from './modals/GestionarSolicitudes.vue';
    import { usePreventaListado } from '@/composables/admin/events/preventa/usePreventaListado';
    import CardsInfoEntradas from '@/modules/admin/events/preventa/CardsInfoEntradas.vue';
    import Riesgo from '@/modules/admin/ui/Riesgo.vue';
    import CriteriosRiesgoPreventa from "./modals/CriteriosRiesgoPreventa.vue";

    const props = defineProps({
        evento_id: {
            type: [String, Number],
            required: true
        }
    });

    const {
        filters,
        loading,
        loadingReenvio,
        registros,
        getRegistrosPreventa,
        getBadgeClass,
        getStatusLabel,
        reenviarCorreo
    } = usePreventaListado(props.evento_id);

    const debouncedGetRegistros = _.debounce(getRegistrosPreventa, 500);

    const { proxy } = getCurrentInstance();

    const showModal = modal => {
        proxy.$modal.show(modal);
    };

    const closeModal = (modalName, recargar) => {
        proxy.$modal.hide(modalName);

        if (recargar) {
            getRegistrosPreventa();
        }
    };

    watch(loadingReenvio, (newVal, oldVal) => {
        if (oldVal === true && newVal === false) {
            proxy.$toast.success('Correo reenviado correctamente');
        }
    });

    const getOptions = registro => {
        const options = [];

        if (registro.status === 'pending' || registro.status === 'rejected') {
            options.push({
                type: 'action',
                action: () => showModal('ver-' + registro.id),
                label: 'Gestionar Preventa'
            });
        }

        if (registro.status === 'selected') {
            options.push({
                type: 'action',
                action: () => reenviarCorreo(registro.id),
                label: 'Reenviar correo'
            });
        }

        if (registro.pedido && registro.pedido.n_pedido && registro.pedido.sesion_id) {
            options.push({
                type: 'action',
                action: () => {
                    window.open(
                        `/admin/eventos/${props.evento_id}/sesiones/${registro.pedido.sesion_id}/ventas?n_pedido=${registro.pedido.n_pedido}`,
                        '_blank'
                    );
                },
                label: 'Ver pedido'
            });
        }

        return options;
    };

    onMounted(() => {
        getRegistrosPreventa();
    });
</script>
<style scoped>
.table {
    overflow: auto;
}
.table.expanded {
    padding-bottom: 150px !important;
}
</style>
